import React from 'react';
import type { TFunction } from 'i18next';
import { XCircle, CheckCircle2, HelpCircle, Plus, ChevronDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { localized } from '../../../utils/i18n-fields';
import { enumLabel } from '../../../utils/i18n-enums';
import { FindingCard, type FindingAccent } from '../sections/FindingCard';
import { SectionDivider } from '../sections/SectionDivider';
import {
  CollapseBtn, CollapseBtnLeft, CollapseBtnChevron,
  StancePill, FindingSourceLink,
} from '../styles';
import i18n from '../../../i18n';

const DEFAULT_VISIBLE = 4;

function claimIcon(status: string, statusColor?: string): LucideIcon {
  if (status === 'verified_false' || statusColor === 'red') return XCircle;
  if (status === 'verified_true' || statusColor === 'green') return CheckCircle2;
  return HelpCircle;
}

function claimAccent(status: string, statusColor?: string): FindingAccent {
  if (status === 'verified_false' || statusColor === 'red') return 'critical';
  if (status === 'verified_true' || statusColor === 'green') return 'success';
  return 'neutral';
}

function stanceFromString(s: string): 'contradicts' | 'supports' | 'neutral' {
  const u = (s || '').toUpperCase();
  if (u === 'CONTRADICTS') return 'contradicts';
  if (u === 'SUPPORTS') return 'supports';
  return 'neutral';
}

function sourceHostname(url: string): string {
  return (url || '').replace(/^https?:\/\/(www\.)?/, '').split('/')[0];
}

export function renderClaimsEditorial(
  data: any,
  t: TFunction,
  expanded: boolean,
  onToggle: () => void,
) {
  if (!data) return null;
  const claims = data.claims_verified || data.claims || [];
  if (claims.length === 0) return null;

  const total = data.total_claims || claims.length;
  const trueCount = data.verified_true || 0;
  const falseCount = data.verified_false || 0;
  const unverified = data.unverified || 0;

  const summaryParts = [
    `${total} ${t('analysis.claims.totalShort')}`,
    trueCount > 0 ? `${trueCount} ${t('analysis.claims.shortTrue')}` : null,
    falseCount > 0 ? `${falseCount} ${t('analysis.claims.shortFalse')}` : null,
    unverified > 0 ? `${unverified} ${t('analysis.claims.shortUnverified')}` : null,
  ].filter(Boolean).join(' · ');

  const visible = expanded ? claims.length : Math.min(DEFAULT_VISIBLE, claims.length);
  const shown = claims.slice(0, visible);
  const remaining = claims.length - visible;

  return (
    <>
      <SectionDivider
        label={t('analysis.claims.verification')}
        count={summaryParts}
      />
      {shown.map((claim: any, i: number) => {
        const icon = claimIcon(claim.status, claim.status_color);
        const accent = claimAccent(claim.status, claim.status_color);
        const statusName = localized(claim, 'status_name') || enumLabel(claim.status || '');
        const typeName = claim.type_name || enumLabel(claim.type || '');
        const eyebrowParts = [
          statusName,
          typeName,
          claim.priority ? `${t('analysis.claims.priority')} ${claim.priority}` : null,
        ].filter(Boolean);
        const sources = (claim.sources || []).slice(0, 6);
        return (
          <FindingCard
            key={claim.id || i}
            icon={icon}
            accent={accent}
            eyebrow={eyebrowParts.join(' · ')}
            headline={claim.text}
            quote={claim.reasoning}
            source={sources.length > 0 ? (
              <>
                {(() => {
                  const dominantStance = sources[0]?.stance ? stanceFromString(sources[0].stance) : 'neutral';
                  return (
                    <StancePill stance={dominantStance}>
                      {t(`analysis.claims.stance.${dominantStance}`)}
                    </StancePill>
                  );
                })()}
                {sources.map((src: any, idx: number) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && <span className="sep">·</span>}
                    <FindingSourceLink href={src.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                      {sourceHostname(src.url)}
                    </FindingSourceLink>
                  </React.Fragment>
                ))}
              </>
            ) : undefined}
          />
        );
      })}
      {remaining > 0 && (
        <CollapseBtn onClick={onToggle}>
          <CollapseBtnLeft>
            <Plus size={16} strokeWidth={2} />
            <span>{t('analysis.claims.showMoreClaims', { count: remaining })}</span>
          </CollapseBtnLeft>
          <CollapseBtnChevron expanded={false}>
            <ChevronDown size={14} strokeWidth={2.5} />
          </CollapseBtnChevron>
        </CollapseBtn>
      )}
      {expanded && claims.length > DEFAULT_VISIBLE && (
        <CollapseBtn onClick={onToggle}>
          <CollapseBtnLeft>
            <ChevronDown size={16} strokeWidth={2} />
            <span>{t('analysis.claims.hide')}</span>
          </CollapseBtnLeft>
          <CollapseBtnChevron expanded={true}>
            <ChevronDown size={14} strokeWidth={2.5} />
          </CollapseBtnChevron>
        </CollapseBtn>
      )}
    </>
  );
}

/** Backward-compat wrapper for History.tsx (no live `t` available there). */
export function renderClaimsDetail(data: any, _t: any) {
  return <RenderClaimsStatic data={data} />;
}

const RenderClaimsStatic: React.FC<{ data: any }> = ({ data }) => {
  const [expanded, setExpanded] = React.useState(false);
  const t = i18n.t.bind(i18n) as TFunction;
  return <>{renderClaimsEditorial(data, t, expanded, () => setExpanded(o => !o))}</>;
};

export function renderClaimsSummary(data: any, t: any) {
  if (!data) return null;
  return (
    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
      {data.total_claims || 0} {t('pipeline.claimsLabel')}
      {data.verified_false > 0 ? ` · ${data.verified_false} ${t('pipeline.falseLabel')}` : ''}
    </span>
  );
}
